import React, { useMemo } from 'react';

import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { ChevronRightIcon } from '@saas-ui/react';

import { Tag } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';
import { JOB_STATUS } from '@/types/jobs';
import { RelatedRecentJob, SearchOutcomeHit } from '@/types/search';
import { formatDateExtended } from '@/utils/dateUtils';
import { getJobStatusCategoryColors } from '@/utils/job';
import {
  formatRelatedJobResult,
  formatRelatedJobStatus,
  resolveRelatedJobId,
} from '@/utils/searchOutcomes';

interface RelatedRecentJobsProps {
  jobs: RelatedRecentJob[] | undefined;
  hits?: SearchOutcomeHit[];
  onOpenJob?: (jobId: number) => void;
}

const WRAP_TAG_SX = {
  whiteSpace: 'normal',
  height: 'auto',
  minH: 6,
  py: 1,
  alignItems: 'flex-start',
  '& .chakra-tag__label': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: '1.25',
  },
} as const;

const isJobStatus = (value: string): value is JOB_STATUS => {
  return Object.values(JOB_STATUS).includes(value as JOB_STATUS);
};

export const RelatedRecentJobs: React.FC<RelatedRecentJobsProps> = ({
  jobs,
  hits,
  onOpenJob,
}) => {
  const rows = useMemo(() => {
    if (!jobs?.length) return [];

    return jobs.slice(0, 3).map((job, index) => {
      const key = `${job.jobId ?? job.ref ?? 'job'}-${index}`;
      const jobId = resolveRelatedJobId(job, hits);
      const statusLabel = formatRelatedJobStatus(job.status);
      const resultLabel = formatRelatedJobResult(job.result);
      const statusColor =
        job.status && isJobStatus(job.status)
          ? getJobStatusCategoryColors(job.status).color
          : 'tags.blue.font';
      const dateLabel = job.completedAt
        ? formatDateExtended(job.completedAt)
        : null;
      const meta = [job.engineer, job.type, job.site, dateLabel]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(' · ');

      return {
        key,
        job,
        jobId,
        statusLabel,
        resultLabel,
        statusColor,
        meta,
      };
    });
  }, [hits, jobs]);

  if (!rows.length) {
    return (
      <Text fontSize='xs' opacity={0.5} color='fonts.primary'>
        No recent jobs available
      </Text>
    );
  }

  return (
    <VStack align='stretch' gap={2} w='full' minW={0} data-cy='related-works-recent'>
      <Text
        fontSize='xs'
        fontWeight='semibold'
        color='tabs.font-selected'
        textTransform='uppercase'
        letterSpacing='0.02em'
      >
        Recent jobs
      </Text>
      {rows.map(row => {
        const canOpen = row.jobId != null && Boolean(onOpenJob);

        return (
          <Box
            key={row.key}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            textAlign='left'
            border='1px solid'
            borderColor='border.light'
            bg='glass-items.bg'
            rounded='xl'
            px={3}
            py={2.5}
            minW={0}
            overflow='hidden'
            cursor={canOpen ? 'pointer' : 'default'}
            transition='background 0.15s ease'
            _hover={canOpen ? { bg: 'glass-items.bg-hover' } : undefined}
            onClick={event => {
              event.stopPropagation();
              if (row.jobId == null || !onOpenJob) return;
              onOpenJob(row.jobId);
            }}
            onKeyDown={event => {
              if (!canOpen) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              if (row.jobId == null || !onOpenJob) return;
              onOpenJob(row.jobId);
            }}
            data-cy='related-works-recent-job'
          >
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify='space-between'
              align={{ base: 'stretch', md: 'flex-start' }}
              gap={2}
              mb={1}
              minW={0}
            >
              <Box minW={0} flex={1}>
                <HStack gap={2} align='center' minW={0}>
                  <Text
                    fontSize='sm'
                    fontWeight='medium'
                    color='fonts.primary'
                    noOfLines={2}
                    minW={0}
                    flex={1}
                  >
                    {row.job.ref || 'Untitled job'}
                  </Text>
                  {canOpen ? (
                    <Box
                      as='span'
                      color='fonts.primary'
                      opacity={0.45}
                      flexShrink={0}
                    >
                      <ChevronRightIcon boxSize='14px' />
                    </Box>
                  ) : null}
                </HStack>
                {row.statusLabel ? (
                  <HStack gap={1.5} mt={0.5} minW={0}>
                    <Box
                      w={1.5}
                      h={1.5}
                      rounded='full'
                      bg={row.statusColor}
                      flexShrink={0}
                    />
                    <Text
                      fontSize='xs'
                      lineHeight='5'
                      fontWeight='semibold'
                      color={row.statusColor}
                      textTransform='uppercase'
                      noOfLines={2}
                    >
                      {row.statusLabel}
                    </Text>
                  </HStack>
                ) : null}
              </Box>
              {row.resultLabel ? (
                <Tag
                  size='sm'
                  isReadOnly
                  alignSelf='flex-start'
                  maxW='100%'
                  flexShrink={1}
                  sx={WRAP_TAG_SX}
                >
                  {row.resultLabel}
                </Tag>
              ) : null}
            </Flex>
            {row.meta ? (
              <Text
                fontSize='xs'
                opacity={0.5}
                color='fonts.primary'
                noOfLines={{ base: 2, xl: 1 }}
                mb={row.job.outcome ? 1 : 0}
              >
                {row.meta}
              </Text>
            ) : null}
            {row.job.outcome ? (
              <Text
                fontSize='xs'
                opacity={FONTS_OPACITY}
                color='fonts.primary'
                noOfLines={2}
              >
                {row.job.outcome}
              </Text>
            ) : null}
          </Box>
        );
      })}
    </VStack>
  );
};
